import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { companyApi } from '../../api/company.api';
import { Badge, Skeleton, Avatar, Btn, Modal, ConfirmDialog, EmptyState, Input } from '../../components/ui/index';
import { useAuth } from '../../store/AuthContext';
import { formatDate } from '../../utils/persianNumber';
import { useUI } from '../../store/UIContext';
import LeafletMapPicker from '../../components/map/LeafletMapPicker';
import CompanyMissionRecipientsSection from '../../components/companies/CompanyMissionRecipientsSection';
import { MapPin, PencilSimple } from '@phosphor-icons/react';

const emptyPlaceForm = { name: '', lat: '', lng: '', address: '' };

// Locations belonging to this one company - view/add/edit/remove, scoped
// here instead of a flat global list with no company context. Previously
// there was no way to see or manage a company's places at all after
// AddPlacePage's one-shot "add" form.
function CompanyPlacesSection({ companyName }) {
  const { addToast } = useUI();
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = adding new, {location_id,...} = editing
  const [form, setForm] = useState(emptyPlaceForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => {
    if (!companyName) return;
    setLoading(true);
    companyApi.getLocationsByCompany(companyName)
      .then((res) => setPlaces(Array.isArray(res.data) ? res.data : []))
      .catch(() => addToast('خطا در دریافت مکان‌های شرکت', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [companyName]);

  const openAdd = () => { setEditing({}); setForm(emptyPlaceForm); };
  const openEdit = (p) => { setEditing(p); setForm({ name: p.name || '', lat: String(p.lat ?? ''), lng: String(p.long ?? ''), address: p.description || '' }); };
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    if (!form.name.trim()) { addToast('نام مکان الزامی است', 'error'); return; }
    if (!form.lat || !form.lng) { addToast('موقعیت را روی نقشه انتخاب کنید', 'error'); return; }
    setSaving(true);
    try {
      if (editing?.location_id) {
        await companyApi.editPlace({ location_id: editing.location_id, company_name: companyName, name: form.name, lat: form.lat, long: form.lng, description: form.address });
        addToast('مکان بروزرسانی شد', 'success');
      } else {
        await companyApi.addPlace({ company_name: companyName, name: form.name, lat: form.lat, long: form.lng, description: form.address });
        addToast('مکان افزوده شد', 'success');
      }
      setEditing(null);
      load();
    } catch { addToast('خطا در ذخیره مکان', 'error'); }
    setSaving(false);
  };

  const remove = async () => {
    const target = deleteTarget;
    try {
      await companyApi.deletePlace(target.location_id);
      addToast('مکان حذف شد', 'success');
      load();
    } catch { addToast('خطا در حذف مکان', 'error'); }
  };

  const inp = { width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' };
  const label = { fontSize: 12, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 5 };

  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid rgba(35,58,122,0.1)', padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h4 style={{ fontSize: 13, fontWeight: 700, color: '#64748b', margin: 0, textTransform: 'uppercase', letterSpacing: 0.5 }}>مکان‌های شرکت</h4>
        <Btn size="sm" onClick={openAdd}>+ افزودن مکان</Btn>
      </div>

      {loading ? (
        <Skeleton h={60} radius={10} />
      ) : places.length === 0 ? (
        <EmptyState icon={<MapPin size={44} weight="regular" />} title="مکانی برای این شرکت ثبت نشده است" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {places.map((p) => (
            <div key={p.location_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#f8faff', border: '1px solid #e2e8f0', borderRadius: 10, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{p.name || 'بدون نام'}</div>
                {p.description && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{p.description}</div>}
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2, direction: 'ltr', textAlign: 'left' }}>{Number(p.lat).toFixed(5)}, {Number(p.long).toFixed(5)}</div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <Btn size="sm" variant="secondary" onClick={() => openEdit(p)}><PencilSimple size={13} weight="regular" /> ویرایش</Btn>
                <Btn size="sm" variant="danger" onClick={() => setDeleteTarget(p)}>🗑️ حذف</Btn>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.location_id ? 'ویرایش مکان' : 'افزودن مکان'} width={560}>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div><label style={label}>نام مکان</label><input value={form.name} onChange={set('name')} style={inp} /></div>
          <div>
            <label style={label}>موقعیت روی نقشه</label>
            <LeafletMapPicker lat={form.lat} lng={form.lng} onChange={(lat, lng) => setForm((p) => ({ ...p, lat: String(lat), lng: String(lng) }))} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><label style={label}>عرض جغرافیایی</label><input value={form.lat} onChange={set('lat')} dir="ltr" style={inp} /></div>
            <div><label style={label}>طول جغرافیایی</label><input value={form.lng} onChange={set('lng')} dir="ltr" style={inp} /></div>
          </div>
          <div><label style={label}>آدرس</label><textarea value={form.address} onChange={set('address')} rows={2} style={{ ...inp, resize: 'vertical' }} /></div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn variant="ghost" onClick={() => setEditing(null)}>انصراف</Btn>
            <Btn onClick={save} loading={saving}>ذخیره</Btn>
          </div>
        </div>
      </Modal>

      <ConfirmDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={remove} danger
        title="حذف مکان" message={`آیا از حذف «${deleteTarget?.name || ''}» مطمئن هستید؟`} />
    </div>
  );
}

export default function CompanyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { addToast } = useUI();
  const canEditCompany = user?.type === 'dispatcher' || user?.type === 'admin';
  // CompanyListPage already has this row's data (name/contract_type/is_active/
  // project_manager/inner_phone_number) from the list endpoint - passed via
  // navigation state so it renders instantly instead of waiting on a fetch.
  const [flags, setFlags] = useState({});
  const listCompany = location.state?.company ? { ...location.state.company, ...flags } : null;
  const toggleFlag = async (field) => {
    const next = !listCompany[field];
    try {
      await companyApi.setCompanyContractStatus({ id, [field]: next ? 1 : 0 });
      setFlags((f) => ({ ...f, [field]: next }));
      addToast('وضعیت شرکت به‌روزرسانی شد', 'success');
    } catch (err) {
      const msg = err.response?.data;
      addToast(typeof msg === 'string' && msg ? msg : 'خطا در تغییر وضعیت', 'error');
    }
  };

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const loadInfo = () => companyApi.getCompanyInfo(id)
    .then((res) => setInfo(res.data || null))
    .catch(() => addToast('خطا در دریافت اطلاعات شرکت', 'error'));

  useEffect(() => {
    loadInfo().finally(() => setLoading(false));
  }, [id]);

  const openEdit = () => {
    setEditName(info?.company_name || listCompany?.name || '');
    setEditDisplayName(info?.display_name && info.display_name !== info.company_name ? info.display_name : '');
    setEditDescription(info?.description || '');
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editName.trim()) {
      addToast('نام شرکت الزامی است', 'error');
      return;
    }
    setSavingEdit(true);
    try {
      await companyApi.editCompanyInfo({
        id,
        name: editName.trim(),
        display_name: editDisplayName.trim(),
        description: editDescription,
      });
      addToast('اطلاعات شرکت به‌روزرسانی شد', 'success');
      setEditOpen(false);
      await loadInfo();
    } catch (err) {
      const msg = err.response?.data;
      addToast(typeof msg === 'string' && msg ? msg : 'خطا در ویرایش شرکت', 'error');
    }
    setSavingEdit(false);
  };

  const realName = info?.company_name || listCompany?.name || '';
  const name = info?.display_name || realName || 'شرکت';

  return (
    <div className="fade-in">
      <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, marginBottom: 20, padding: 0 }}>
        ← بازگشت به لیست شرکت‌ها
      </button>

      {loading && !listCompany ? (
        <div style={{ background: '#fff', borderRadius: 16, padding: 24 }}>
          <Skeleton h={22} w="50%" style={{ marginBottom: 12 }} />
          <Skeleton h={14} /><Skeleton h={14} w="80%" />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 720 }}>
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(35,58,122,0.1)', boxShadow: '0 4px 16px rgba(35,58,122,0.08)', padding: 24, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
              <Avatar name={name.slice(0, 2)} size={56} />
              <div style={{ minWidth: 0 }}>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', margin: '0 0 6px' }}>{name}</h2>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {listCompany && <Badge variant={listCompany.is_active ? 'green' : 'gray'}>{listCompany.is_active ? 'فعال' : 'غیرفعال'}</Badge>}
                  {listCompany && <Badge variant={listCompany.has_contract ? 'green' : 'amber'}>{listCompany.has_contract ? 'دارای قرارداد' : 'بدون قرارداد'}</Badge>}
                  {listCompany?.contract_type && <Badge variant="blue">{listCompany.contract_type}</Badge>}
                </div>
                {canEditCompany && listCompany && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                    <Btn size="sm" variant="ghost" onClick={() => toggleFlag('is_active')}>{listCompany.is_active ? 'غیرفعال کردن شرکت' : 'فعال کردن شرکت'}</Btn>
                    <Btn size="sm" variant="ghost" onClick={() => toggleFlag('has_contract')}>{listCompany.has_contract ? 'لغو قرارداد' : 'ثبت قرارداد'}</Btn>
                  </div>
                )}
              </div>
            </div>
            {canEditCompany && (
              <Btn size="sm" variant="secondary" onClick={openEdit} style={{ flexShrink: 0 }}>
                <PencilSimple size={16} weight="bold" style={{ marginLeft: 6 }} />
                ویرایش نام / توضیحات
              </Btn>
            )}
          </div>

          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid rgba(35,58,122,0.1)', padding: 20 }}>
            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#64748b', marginBottom: 14, textTransform: 'uppercase', letterSpacing: 0.5 }}>اطلاعات شرکت</h4>
            {[
              { label: 'مدیر پروژه', value: listCompany?.project_manager },
              { label: 'طرف قرارداد', value: info?.contract_party || listCompany?.contract_party },
              { label: 'تلفن داخلی', value: listCompany?.inner_phone_number },
              { label: 'پایان قرارداد', value: info?.contract_end_time ? formatDate(info.contract_end_time) : null },
            ].filter((row) => row.value).map(({ label, value }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
                <span style={{ color: '#94a3b8' }}>{label}</span>
                <span style={{ color: '#334155', fontWeight: 600 }}>{value}</span>
              </div>
            ))}
          </div>

          {realName && <CompanyPlacesSection companyName={realName} />}

          {id && <CompanyMissionRecipientsSection companyId={Number(id)} />}

          {(info?.description || canEditCompany) && (
            <div style={{ background: '#fff', borderRadius: 14, border: '1px solid rgba(35,58,122,0.10)', padding: 20 }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: '#64748b', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>توضیحات</h4>
              <p style={{ fontSize: 14, color: '#334155', lineHeight: 1.8, margin: 0, whiteSpace: 'pre-wrap' }}>
                {info?.description || (canEditCompany ? '—' : '')}
              </p>
            </div>
          )}
        </div>
      )}

      {editOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}
        >
          <div style={{
            background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 420,
            maxHeight: '90vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }}
          >
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 8px', color: '#0f172a' }}>ویرایش شرکت</h3>
            <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 16px', lineHeight: 1.7 }}>
              با تغییر نام شرکت، پیشوند نام کاربری مشتریان همان شرکت به‌روز می‌شود؛ رمز عبور آن‌ها تغییر نمی‌کند.
            </p>
            <Input label="نام شرکت (پیشوند نام کاربری) *" value={editName} onChange={(e) => setEditName(e.target.value)} />
            <div style={{ marginTop: 12 }}>
              <Input label="نام نمایشی" placeholder="نامی که به مسئولان نمایش داده می‌شود (اختیاری)" value={editDisplayName} onChange={(e) => setEditDisplayName(e.target.value)} />
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 6 }}>توضیحات</label>
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                rows={4}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '10px 14px', border: '1.5px solid #e2e8f0',
                  borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <Btn variant="secondary" onClick={() => setEditOpen(false)} disabled={savingEdit}>انصراف</Btn>
              <Btn onClick={saveEdit} loading={savingEdit}>ذخیره</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
